import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LoadingScreen from '../LoadingScreen';
import AuthMethodSelect from './AuthMethodSelect';
import EmailInputStep from './EmailInputStep';
import UsernameStep from './UsernameStep';
import AgeVerifyStep from './AgeVerifyStep';
import CountryStep from './CountryStep';
import CodeVerifyStep from './CodeVerifyStep';
import OAuthLoadingStep from './OAuthLoadingStep';
import logoMarkUrl from '../../assets/landing/logo-mark.svg';
import middleUrl from '../../assets/landing/middle.svg';
import backgroundVectorsUrl from '../../assets/landing/background-vectors.svg';
import spadeVectorsUrl from '../../assets/landing/spade-vectors.svg';
import lightBehindSpadeUrl from '../../assets/landing/light-behind-spade.svg';
import topRectangleBgUrl from '../../assets/landing/top-rectangle-bg.svg';
import bottomRectangleUrl from '../../assets/landing/bottom-rectangle.svg';
import lineSeparatorUrl from '../../assets/landing/line-separator.svg';
import { ROUTES } from '../../constants';
import {
  authApi,
  readStoredToken,
  writeStoredToken,
  writeStoredUser,
} from '../../services/authApi';
import { resolvePostAuthRoute } from '../../utils/postAuthRoute';
import './UnifiedAuthPage.css';

/**
 * Step state machine:
 *
 * METHOD_SELECT
 * ├─ Twitch/Google → redirect to backend → callback
 * │   → (existing user) → SUCCESS
 * │   → (new user) → OAUTH_USERNAME → OAUTH_AGE → OAUTH_COUNTRY → SUCCESS
 * ├─ Email → EMAIL_INPUT
 * │   → (existing) → EMAIL_CODE → SUCCESS
 * │   → (new) → EMAIL_USERNAME → EMAIL_AGE → EMAIL_COUNTRY → EMAIL_CODE → SUCCESS
 * SUCCESS → LoadingScreen → navigate('/home')
 */

const STEPS = {
  METHOD_SELECT: 'METHOD_SELECT',
  OAUTH_LOADING: 'OAUTH_LOADING',
  OAUTH_USERNAME: 'OAUTH_USERNAME',
  OAUTH_AGE: 'OAUTH_AGE',
  OAUTH_COUNTRY: 'OAUTH_COUNTRY',
  EMAIL_INPUT: 'EMAIL_INPUT',
  EMAIL_USERNAME: 'EMAIL_USERNAME',
  EMAIL_AGE: 'EMAIL_AGE',
  EMAIL_COUNTRY: 'EMAIL_COUNTRY',
  EMAIL_CODE: 'EMAIL_CODE',
  SUCCESS: 'SUCCESS',
};

const OAUTH_REDIRECT_FLAG = 'oauth_redirecting';

function UnifiedAuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const midRef = useRef(null);
  const [isPortraitLayout, setIsPortraitLayout] = useState(false);

  const [step, setStep] = useState(location.state?.step || STEPS.METHOD_SELECT);
  const [oauthToken] = useState(location.state?.token || readStoredToken() || null);

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [dob, setDob] = useState({ day: '', month: '', year: '' });
  const [country, setCountry] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [platform, setPlatform] = useState(location.state?.provider || null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [emailCodeDeliveryError, setEmailCodeDeliveryError] = useState(null);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateScale = () => {
      const mid = midRef.current;
      if (!mid) {
        return;
      }

      const rect = mid.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      // Scale so the active content band (rows 128-1004) always fits.
      const scale = Math.min(rect.width / 1920, rect.height / 876);
      const stageTop = -(128 * scale);
      const stageLeft = rect.width / 2 - 960 * scale;
      const portrait = window.matchMedia('(orientation: portrait)').matches;

      mid.style.setProperty('--ua-stage-scale', String(scale));
      mid.style.setProperty('--ua-stage-top', `${stageTop}px`);
      mid.style.setProperty('--ua-stage-left', `${stageLeft}px`);
      setIsPortraitLayout((current) => (current === portrait ? current : portrait));
    };

    updateScale();

    const observer = new ResizeObserver(updateScale);
    if (midRef.current) {
      observer.observe(midRef.current);
    }
    window.addEventListener('resize', updateScale);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, []);

  const getOAuthRegistrationToken = useCallback(() => {
    return readStoredToken() || oauthToken || null;
  }, [oauthToken]);

  useEffect(() => {
    if (!sessionStorage.getItem(OAUTH_REDIRECT_FLAG)) {
      return;
    }

    sessionStorage.removeItem(OAUTH_REDIRECT_FLAG);
    setStep(STEPS.METHOD_SELECT);
  }, []);

  useEffect(() => {
    const resetIfOAuthLoading = () => {
      setStep((current) => {
        if (current !== STEPS.OAUTH_LOADING) {
          return current;
        }
        sessionStorage.removeItem(OAUTH_REDIRECT_FLAG);
        return STEPS.METHOD_SELECT;
      });
    };

    const handlePageShow = (event) => {
      if (event.persisted) {
        resetIfOAuthLoading();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        resetIfOAuthLoading();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const activeOAuthToken = getOAuthRegistrationToken();
    if (!activeOAuthToken) {
      return undefined;
    }

    let cancelled = false;

    const loadOAuthUserContext = async () => {
      try {
        const response = await authApi.getCurrentUser(activeOAuthToken);
        const user = response?.user || {};

        if (cancelled) {
          return;
        }

        if (user.email) {
          setEmail((current) => current || user.email);
        }
        if (user.firstName) {
          setFirstName((current) => current || user.firstName);
        }
        if (user.lastName) {
          setLastName((current) => current || user.lastName);
        }
      } catch (error) {
        console.error('Failed to load OAuth user context:', error);
      }
    };

    loadOAuthUserContext();

    return () => {
      cancelled = true;
    };
  }, [getOAuthRegistrationToken]);

  const handleOAuthSelect = useCallback((selectedPlatform) => {
    setPlatform(selectedPlatform);
    setStep(STEPS.OAUTH_LOADING);
    sessionStorage.setItem(OAUTH_REDIRECT_FLAG, 'true');

    if (selectedPlatform === 'twitch') {
      window.location.href = authApi.getTwitchAuthUrl();
    } else if (selectedPlatform === 'google') {
      window.location.href = authApi.getGoogleAuthUrl();
    }
  }, []);

  const handleOAuthUsernameConfirm = useCallback((confirmedUsername) => {
    setUsername(confirmedUsername);
    setStep(STEPS.OAUTH_AGE);
  }, []);

  const handleOAuthAgeConfirm = useCallback((confirmedDob) => {
    setDob(confirmedDob);
    setStep(STEPS.OAUTH_COUNTRY);
  }, []);

  const handleOAuthCountryConfirm = useCallback(async (confirmedCountry) => {
    const activeOAuthToken = getOAuthRegistrationToken();
    if (!activeOAuthToken) {
      setStep(STEPS.METHOD_SELECT);
      return;
    }

    try {
      setCountry(confirmedCountry);
      writeStoredToken(activeOAuthToken);

      const dateOfBirth = `${dob.year}-${dob.month.padStart(2, '0')}-${dob.day.padStart(2, '0')}`;
      const response = await authApi.completeRegistration(activeOAuthToken, {
        username,
        dateOfBirth,
        country: confirmedCountry,
        firstName,
        lastName,
      });

      if (response.success) {
        writeStoredToken(response.token);
        writeStoredUser(response.user);
        setStep(STEPS.SUCCESS);
      } else {
        setStep(STEPS.METHOD_SELECT);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setStep(STEPS.METHOD_SELECT);
    }
  }, [dob, firstName, getOAuthRegistrationToken, lastName, username]);

  const checkUsernameAvailabilityApi = useCallback(async (name) => {
    const result = await authApi.checkUsername(name);
    return result.available;
  }, []);

  const handleEmailSelect = useCallback(() => {
    setEmailCodeDeliveryError(null);
    setStep(STEPS.EMAIL_INPUT);
  }, []);

  const handleEmailContinue = useCallback(async ({ email: confirmedEmail, isNewUser: newUser }) => {
    setEmail(confirmedEmail);
    setIsNewUser(newUser);

    if (newUser) {
      setEmailCodeDeliveryError(null);
      setStep(STEPS.EMAIL_USERNAME);
      return;
    }

    try {
      await authApi.sendEmailCode(confirmedEmail);
      setEmailCodeDeliveryError(null);
    } catch (error) {
      console.error('Failed to send code:', error);
      setEmailCodeDeliveryError(error?.message || 'Failed to send code');
    }
    setStep(STEPS.EMAIL_CODE);
  }, []);

  const handleEmailUsernameConfirm = useCallback((confirmedUsername) => {
    setUsername(confirmedUsername);
    setStep(STEPS.EMAIL_AGE);
  }, []);

  const handleEmailAgeConfirm = useCallback((confirmedDob) => {
    setDob(confirmedDob);
    setStep(STEPS.EMAIL_COUNTRY);
  }, []);

  const handleEmailCountryConfirm = useCallback(async (confirmedCountry) => {
    setCountry(confirmedCountry);

    try {
      await authApi.sendEmailCode(email);
      setEmailCodeDeliveryError(null);
    } catch (error) {
      console.error('Failed to send code:', error);
      setEmailCodeDeliveryError(error?.message || 'Failed to send code');
    }
    setStep(STEPS.EMAIL_CODE);
  }, [email]);

  const handleCodeVerify = useCallback(async (code) => {
    setEmailCodeDeliveryError(null);

    const response = await authApi.verifyEmailCode(email, code);
    if (!response.success) {
      throw new Error('Verification failed');
    }

    writeStoredToken(response.token);

    if (isNewUser) {
      const dateOfBirth = `${dob.year}-${dob.month.padStart(2, '0')}-${dob.day.padStart(2, '0')}`;
      const regResponse = await authApi.completeRegistration(response.token, {
        username,
        dateOfBirth,
        country,
        firstName,
        lastName,
      });

      if (regResponse.success) {
        writeStoredToken(regResponse.token);
        writeStoredUser(regResponse.user);
      }
    } else if (response.user) {
      writeStoredUser(response.user);
    }

    setStep(STEPS.SUCCESS);
  }, [country, dob, email, firstName, isNewUser, lastName, username]);

  const handleResendCode = useCallback(async () => {
    try {
      await authApi.sendEmailCode(email);
      setEmailCodeDeliveryError(null);
    } catch (error) {
      console.error('Failed to resend code:', error);
      setEmailCodeDeliveryError(error?.message || 'Failed to resend code');
    }
  }, [email]);

  const handleLoadingComplete = useCallback(async () => {
    const nextRoute = await resolvePostAuthRoute();
    navigate(nextRoute);
  }, [navigate]);

  const goToMethodSelect = useCallback(() => setStep(STEPS.METHOD_SELECT), []);
  const goToEmailInput = useCallback(() => setStep(STEPS.EMAIL_INPUT), []);
  const goToEmailUsername = useCallback(() => setStep(STEPS.EMAIL_USERNAME), []);
  const goToOAuthUsername = useCallback(() => setStep(STEPS.OAUTH_USERNAME), []);
  const goToOAuthAge = useCallback(() => setStep(STEPS.OAUTH_AGE), []);
  const goToEmailAge = useCallback(() => setStep(STEPS.EMAIL_AGE), []);

  const checkEmailExistsApi = useCallback(async (emailToCheck) => {
    return authApi.checkEmail(emailToCheck);
  }, []);

  const renderStep = () => {
    switch (step) {
      case STEPS.METHOD_SELECT:
        return (
          <AuthMethodSelect
            onSelectTwitch={() => handleOAuthSelect('twitch')}
            onSelectGoogle={() => handleOAuthSelect('google')}
            onSelectEmail={handleEmailSelect}
          />
        );

      case STEPS.OAUTH_LOADING:
        return <OAuthLoadingStep platform={platform} />;

      case STEPS.OAUTH_USERNAME:
        return (
          <UsernameStep
            username={username}
            onUsernameChange={setUsername}
            onContinue={handleOAuthUsernameConfirm}
            onBack={goToMethodSelect}
            checkUsernameAvailability={checkUsernameAvailabilityApi}
          />
        );

      case STEPS.OAUTH_AGE:
        return (
          <AgeVerifyStep
            dob={dob}
            onDobChange={setDob}
            onContinue={handleOAuthAgeConfirm}
            onBack={goToOAuthUsername}
          />
        );

      case STEPS.OAUTH_COUNTRY:
        return (
          <CountryStep
            country={country}
            onCountryChange={setCountry}
            onContinue={handleOAuthCountryConfirm}
            onBack={goToOAuthAge}
          />
        );

      case STEPS.EMAIL_INPUT:
        return (
          <EmailInputStep
            email={email}
            onEmailChange={setEmail}
            onContinue={handleEmailContinue}
            onBack={goToMethodSelect}
            checkEmailExists={checkEmailExistsApi}
          />
        );

      case STEPS.EMAIL_USERNAME:
        return (
          <UsernameStep
            username={username}
            onUsernameChange={setUsername}
            onContinue={handleEmailUsernameConfirm}
            onBack={goToEmailInput}
            checkUsernameAvailability={checkUsernameAvailabilityApi}
          />
        );

      case STEPS.EMAIL_AGE:
        return (
          <AgeVerifyStep
            dob={dob}
            onDobChange={setDob}
            onContinue={handleEmailAgeConfirm}
            onBack={goToEmailUsername}
          />
        );

      case STEPS.EMAIL_COUNTRY:
        return (
          <CountryStep
            country={country}
            onCountryChange={setCountry}
            onContinue={handleEmailCountryConfirm}
            onBack={goToEmailAge}
          />
        );

      case STEPS.EMAIL_CODE:
        return (
          <CodeVerifyStep
            email={email}
            onVerify={handleCodeVerify}
            onBack={isNewUser ? goToEmailUsername : goToEmailInput}
            onResend={handleResendCode}
            deliveryError={emailCodeDeliveryError}
          />
        );

      case STEPS.SUCCESS:
      default:
        return null;
    }
  };

  const showLoadingScreen = step === STEPS.SUCCESS;

  return (
    <>
      <LoadingScreen
        isVisible={showLoadingScreen}
        onComplete={handleLoadingComplete}
      />

      <div className={`unified-auth ${showLoadingScreen ? 'unified-auth--hidden' : ''}`}>
        <div className="ua-top">
          <img className="ua-top__bg" src={topRectangleBgUrl} alt="" aria-hidden="true" />
          <Link to={ROUTES.WELCOME} className="ua-top__brand" aria-label="Go to Polar Ace home">
            <img className="ua-top__logo" src={logoMarkUrl} alt="Polar Ace" />
          </Link>
          <img className="ua-top__line" src={lineSeparatorUrl} alt="" aria-hidden="true" />
        </div>

        <div className="ua-mid" ref={midRef}>
          {!isPortraitLayout && (
            <div className="ua-stage">
              <img className="ua-stage__bg" src={middleUrl} alt="" aria-hidden="true" />
              <div className="ua-stage__card unified-auth__card">
                {renderStep()}
              </div>
            </div>
          )}

          {isPortraitLayout && (
            <>
              <div className="ua-portrait-bg" aria-hidden="true">
                <img
                  className="ua-portrait-bg__vectors"
                  src={backgroundVectorsUrl}
                  alt=""
                />
                <img
                  className="ua-portrait-bg__glow"
                  src={lightBehindSpadeUrl}
                  alt=""
                />
                <img
                  className="ua-portrait-bg__spade"
                  src={spadeVectorsUrl}
                  alt=""
                />
              </div>

              <div className="ua-portrait-stack">
                <div className="ua-portrait-copy">
                  <h1 className="ua-portrait-copy__title">
                    <span>MEET YOUR</span>
                    <span>FAVORITE</span>
                    <span>CREATORS WITH</span>
                    <span className="ua-portrait-copy__title-accent">POLAR ACE.</span>
                  </h1>

                  <div className="ua-portrait-copy__panel">
                    <p>
                      Polar Ace is a cross-platform product that enables fans to Go Live
                      in a livestream, send a Video Message live, and send a GIF live.
                    </p>
                    <p>
                      Additionally, we will have an added layer of fans or advertisers
                      sending a paid message, that the creator can choose to respond to
                      for more revenue.
                    </p>
                  </div>
                </div>

                <div className="ua-portrait-card unified-auth__card">
                  {renderStep()}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="ua-bot" aria-hidden="true">
          <img className="ua-bot__line" src={lineSeparatorUrl} alt="" aria-hidden="true" />
          <img className="ua-bot__bg" src={bottomRectangleUrl} alt="" aria-hidden="true" />
        </div>
      </div>
    </>
  );
}

export default UnifiedAuthPage;
